import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryWorkflowType,
  useAssignTaskToUser,
  useGetAutopilotTasksForCampaign,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client'
import {
  AgxBadge,
  AgxDivider,
  AgxSpacer,
  AgxTab,
  AgxTypography,
  Icon_Chevron,
  RenderAnimation,
  RenderAnimationType,
} from '@urbanx/agx.ui-components2'
import {
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  Fragment,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  findFirstWorkflowStage,
  getDefaultSelectedWorkflowStage,
  getNextInProgressWorkflowStage,
  getWorkflowBadges,
  type WorkflowStageIndicator,
  WorkflowStageIndicatorStatus,
  sortTasksByCompletionThenOrder,
} from 'utils/tasks'
import { useGetAssigneeOptions } from 'pages/allTasks/TaskFilters/useGetAssigneeOptions'
import Panel from 'components/panel/Panel'
import TaskCollapsible from './TaskCollapsible'
import { TaskBlocker } from 'pages/allTasks/TaskBlockers'
import './Tasks.scss'

export enum TaskTabs {
  Tasks = 'Tasks',
  Deposits = 'Deposits',
  Other = 'Other',
}

export interface TasksProps {
  campaignId: string
  contractId?: string
  primaryWorkflowType: TaskCategoryWorkflowType
  // Filter function to exclude modal tasks
  taskFilter?: (task: AutopilotTaskDto) => boolean
  // Render prop for modal tasks
  renderAdditionalContent?: (params: {
    selectedTab: TaskTabs
    selectedWorkflowStage?: TaskCategoryStage
    allTasksCompleted: boolean
    tasks: AutopilotTaskDto[]
    isViewingCompletedStage: boolean
  }) => ReactNode
  onTasksChange?: (tasks: AutopilotTaskDto[]) => void
}

export interface TasksHandle {
  invalidateTasks: () => void
  tasks: AutopilotTaskDto[]
  allTasksCompleted: boolean
}

const filterTasksForStage = (
  tasks: AutopilotTaskDto[],
  stage?: TaskCategoryStage
) =>
  tasks
    .filter(
      (task) =>
        task.category?.stage === stage && (task.isCompleted || !task.isLocked)
    )
    .sort(sortTasksByCompletionThenOrder)

const Tasks = ({
  campaignId,
  contractId,
  primaryWorkflowType,
  taskFilter,
  renderAdditionalContent,
  onTasksChange,
}: TasksProps) => {
  const { hash } = useLocation()
  const navigate = useNavigate()
  const selectedTab =
    (new URLSearchParams(hash.replace(/^#/, '')).get('tab') as TaskTabs) ??
    TaskTabs.Tasks

  const [selectedWorkflowStage, setSelectedWorkflowStage] = useState<
    TaskCategoryStage | undefined
  >()
  const [isViewingCompletedStage, setIsViewingCompletedStage] = useState(false)

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
  const { assigneeOptions } = useGetAssigneeOptions()

  const { assignTaskToUser } = useAssignTaskToUser({
    onSuccess: () => {
      invalidateTasks(campaignId, contractId)
    },
    onError: () => {},
  })

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: () => {
      invalidateTasks(campaignId, contractId)
    },
    onError: () => {},
  })

  const { data: autoPilotTasks } = useGetAutopilotTasksForCampaign(
    campaignId,
    contractId,
    !!campaignId && (contractId ? !!contractId : true)
  )

  const tasks = autoPilotTasks ?? []

  const primaryTasks = useMemo(
    () =>
      tasks.filter(
        (task) => task.category?.workflowType === primaryWorkflowType
      ),
    [tasks, primaryWorkflowType]
  )

  const allTasksCompleted = useMemo(
    () =>
      primaryTasks.length > 0 && primaryTasks.every((task) => task.isCompleted),
    [primaryTasks]
  )

  const workflowBadges = useMemo(
    () => getWorkflowBadges(primaryTasks as Required<AutopilotTaskDto>[]),
    [primaryTasks]
  )
  const depositTasks = useMemo(
    () =>
      tasks
        .filter(
          (task) =>
            task.category?.stage === TaskCategoryStage.Deposits &&
            task.category?.workflowType !== primaryWorkflowType
        )
        .sort(sortTasksByCompletionThenOrder),
    [tasks, primaryWorkflowType]
  )

  const otherTasks = useMemo(
    () =>
      tasks
        .filter(
          (task) =>
            (task.category?.workflowType ===
              TaskCategoryWorkflowType.IndependentCampaign ||
              task.category?.workflowType ===
                TaskCategoryWorkflowType.IndependentContract) &&
            task.category?.stage !== TaskCategoryStage.Deposits
        )
        .sort(sortTasksByCompletionThenOrder),
    [tasks]
  )

  useEffect(() => {
    if (!campaignId) return
    if (contractId !== undefined && !contractId) return
    invalidateTasks(campaignId, contractId)
  }, [campaignId, contractId, invalidateTasks])

  useEffect(() => {
    if (isViewingCompletedStage || workflowBadges.length === 0) return

    if (!selectedWorkflowStage) {
      const defaultStage = getDefaultSelectedWorkflowStage(workflowBadges)
      if (defaultStage) {
        setSelectedWorkflowStage(defaultStage)
      }
      return
    }

    const currentBadge = workflowBadges.find(
      (b) => b.stage === selectedWorkflowStage
    )
    if (currentBadge?.status === WorkflowStageIndicatorStatus.COMPLETED) {
      const nextInProgressStage = getNextInProgressWorkflowStage(workflowBadges)
      if (nextInProgressStage) {
        setSelectedWorkflowStage(nextInProgressStage)
      }
    }
  }, [workflowBadges, selectedWorkflowStage, isViewingCompletedStage])

  // Keep a stable ref so the effect doesn't re-run when the parent passes
  // a new (non-memoized) callback reference on each render.
  const onTasksChangeRef = useRef(onTasksChange)
  onTasksChangeRef.current = onTasksChange

  useEffect(() => {
    if (autoPilotTasks) {
      onTasksChangeRef.current?.(autoPilotTasks)
    }
  }, [autoPilotTasks])

  const handleBadgeClick = useCallback((badge: WorkflowStageIndicator) => {
    if (badge.status === WorkflowStageIndicatorStatus.LOCKED || !badge.stage)
      return

    setSelectedWorkflowStage(badge.stage)
    setIsViewingCompletedStage(
      badge.status === WorkflowStageIndicatorStatus.COMPLETED
    )
  }, [])

  const displayTasks = useMemo(() => {
    const stageToShow =
      selectedWorkflowStage ?? findFirstWorkflowStage(workflowBadges)
    const filtered = filterTasksForStage(primaryTasks, stageToShow)

    return taskFilter ? filtered.filter(taskFilter) : filtered
  }, [primaryTasks, selectedWorkflowStage, taskFilter, workflowBadges])

  const depositIncompleteCount = useMemo(
    () => depositTasks.filter((x) => !x.isCompleted).length,
    [depositTasks]
  )

  const otherIncompleteCount = useMemo(
    () => otherTasks.filter((x) => !x.isCompleted).length,
    [otherTasks]
  )

  const tabOptions = useMemo(
    () => [
      {
        id: TaskTabs.Tasks,
        label: TaskTabs.Tasks,
        selected: selectedTab === TaskTabs.Tasks,
      },
      ...(depositTasks.length > 0
        ? [
            {
              id: TaskTabs.Deposits,
              label: TaskTabs.Deposits,
              selected: selectedTab === TaskTabs.Deposits,
              tag: depositIncompleteCount.toString(),
            },
          ]
        : []),
      {
        id: TaskTabs.Other,
        label: TaskTabs.Other,
        selected: selectedTab === TaskTabs.Other,
        tag: otherIncompleteCount.toString(),
      },
    ],
    [
      selectedTab,
      depositTasks.length,
      depositIncompleteCount,
      otherIncompleteCount,
    ]
  )

  const renderTask = useCallback(
    (task: AutopilotTaskDto) => (
      <TaskCollapsible
        key={`${task.campaignId}-${task.contractId}-${task.category?.taskType}`}
        task={task}
        contractId={contractId}
        assigneeOptions={assigneeOptions}
        assignTask={assignTaskToUser}
        toggleTaskSteps={toggleTaskSteps}
        renderTaskContent={({ task: currentTask, children }) => (
          <TaskBlocker
            task={currentTask}
            onTasksInvalidated={() => invalidateTasks(campaignId, contractId)}
          >
            {children}
          </TaskBlocker>
        )}
      />
    ),
    [
      assigneeOptions,
      assignTaskToUser,
      campaignId,
      contractId,
      invalidateTasks,
      toggleTaskSteps,
    ]
  )

  return (
    <AgxSpacer fill>
      <AgxSpacer direction="vertical" fill gap="spacing-2xl">
        <AgxSpacer
          id="subPageHeadingContainer"
          direction="vertical"
          fill
          gap="sm"
        >
          <AgxSpacer
            direction="horizontal"
            fill
            contentJustification="space-between"
            contentAlignment="center"
          >
            <AgxTab
              onChange={(tab) =>
                navigate({ hash: `tab=${tab.id}` }, { replace: true })
              }
              options={tabOptions}
            />
            {selectedTab === TaskTabs.Tasks && (
              <AgxSpacer
                fill
                direction="horizontal"
                contentJustification="end"
                contentAlignment="center"
              >
                {workflowBadges.map((badge, index) => {
                  const isClickable =
                    badge.status !== WorkflowStageIndicatorStatus.LOCKED &&
                    badge.stage !== undefined
                  return (
                    <Fragment key={`${badge.stage ?? 'none'}-${badge.name}`}>
                      <AgxBadge
                        type="colourfill"
                        colour={badge.color}
                        size="md"
                        text={badge.name}
                        variant={isClickable ? 'clickable' : 'normal'}
                        onClick={
                          isClickable
                            ? () => handleBadgeClick(badge)
                            : undefined
                        }
                        hoverBackgroundColor={
                          isClickable &&
                          badge.status ===
                            WorkflowStageIndicatorStatus.COMPLETED
                            ? 'var(--bg-progress)'
                            : undefined
                        }
                      />
                      {index < workflowBadges.length - 1 && (
                        <Icon_Chevron
                          width={18}
                          height={18}
                          direction={'right'}
                        />
                      )}
                    </Fragment>
                  )
                })}
              </AgxSpacer>
            )}
          </AgxSpacer>
          <div className="workflowTask-divider-stretch">
            <AgxDivider variant="horizontal" />
          </div>
        </AgxSpacer>
        {selectedTab === TaskTabs.Tasks &&
          allTasksCompleted &&
          !isViewingCompletedStage && (
            <AgxSpacer
              direction="horizontal"
              fill
              contentJustification="center"
            >
              <Panel
                padding={{
                  bottom: 'xl',
                }}
              >
                <AgxSpacer
                  fill
                  contentJustification="center"
                  gap="small"
                  contentAlignment="center"
                >
                  <RenderAnimation
                    icon={RenderAnimationType.Champagne}
                    loop={false}
                  />
                  <AgxTypography
                    id="allTasksCompleted"
                    type="body"
                    size="medium"
                  >
                    All {primaryWorkflowType.toLowerCase()} tasks are completed
                  </AgxTypography>
                </AgxSpacer>
              </Panel>
            </AgxSpacer>
          )}

        {selectedTab === TaskTabs.Tasks &&
          (!allTasksCompleted || isViewingCompletedStage) &&
          displayTasks.map(renderTask)}

        {selectedTab === TaskTabs.Deposits && depositTasks.map(renderTask)}

        {selectedTab === TaskTabs.Other && otherTasks.map(renderTask)}

        {renderAdditionalContent?.({
          selectedTab,
          selectedWorkflowStage,
          allTasksCompleted,
          tasks,
          isViewingCompletedStage,
        })}
      </AgxSpacer>
    </AgxSpacer>
  )
}

export default Tasks
